import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { t } from '../hooks/useT';

const VERIFY_ERRORS: Record<string, string> = {
  token_required: 'Lien invalide — il manque le jeton de connexion.',
  invalid_token: 'Ce lien n\'est plus valide. Demande-en un nouveau (un seul lien actif à la fois).',
  token_expired: 'Ce lien a expiré (15 min). Demande-en un nouveau.',
  rate_limit: 'Trop de tentatives — réessaie dans quelques minutes.',
  server_error: 'Erreur serveur — réessaie dans un instant.',
};

export function AuthVerifyPage() {
  const [params] = useSearchParams();
  const { loginWithToken } = useAuth();
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const token = params.get('token');

  const handleConfirm = async () => {
    if (!token) {
      setErrorMsg(VERIFY_ERRORS.token_required);
      return;
    }
    setBusy(true);
    setErrorMsg('');
    try {
      const res = await api<{ token: string }>(`/auth/verify?token=${encodeURIComponent(token)}`);
      await loginWithToken(res.token);
      window.location.replace('/play');
    } catch (err: unknown) {
      const e = err as { data?: { error?: string }; status?: number };
      const code = e.data?.error;
      setErrorMsg(
        (code && VERIFY_ERRORS[code])
          || (e.status === 429 ? VERIFY_ERRORS.rate_limit : VERIFY_ERRORS.server_error),
      );
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <main className="page login">
        <div className="login-card">
          <h1>Connexion impossible</h1>
          <p className="login-sub">{VERIFY_ERRORS.token_required}</p>
          <Link to="/login" className="btn primary block">Demander un nouveau lien</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="page login">
      <div className="login-card">
        <p className="login-brand">Dezoom</p>
        <h1>{t('auth.verifyTitle')}</h1>
        <p className="login-sub">{t('auth.verifySub')}</p>

        {errorMsg && <p className="error login-error" role="alert">{errorMsg}</p>}

        <button
          type="button"
          className="btn primary block"
          onClick={handleConfirm}
          disabled={busy}
        >
          {busy ? t('auth.verifyBusy') : t('auth.verifyConfirm')}
        </button>

        <div className="login-footer">
          <Link to="/login" className="link-muted login-back">{t('auth.verifyNewLink')}</Link>
        </div>
      </div>
    </main>
  );
}

export function AuthCallbackPage() {
  const [params] = useSearchParams();
  const { loginWithToken } = useAuth();
  const [errorMsg, setErrorMsg] = useState('');
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const token = params.get('token');
    if (!token) {
      setErrorMsg(VERIFY_ERRORS.token_required);
      return;
    }
    loginWithToken(token)
      .then(() => { window.location.replace('/play'); })
      .catch(() => setErrorMsg(VERIFY_ERRORS.server_error));
  }, [params, loginWithToken]);

  if (errorMsg) {
    return (
      <main className="page login">
        <div className="login-card">
          <h1>Connexion impossible</h1>
          <p className="login-sub">{errorMsg}</p>
          <Link to="/login" className="btn primary block">Retour à la connexion</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="page login">
      <div className="login-card">
        <p className="loading">Connexion en cours…</p>
      </div>
    </main>
  );
}
